import { onlyNumbersRegex, onlyTextRegex } from '@constants/general';
import {
  Autocomplete,
  Box,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import GENDER_OPTIONS from '@pages/onboarding/Mexico/constants/genderOptions';
import {
  eighteenYearsAgo,
  TEXTS_MAX_LENGTH,
} from '@pages/onboarding/USA/constants';
import { DateTime } from 'luxon';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CountriesList } from '@efex-services/auth/types';
import authSvc from '@efex-services/auth/authSvc';
import { useApiCall } from 'use-api-call';
import { useEffect, useState, useCallback } from 'react';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Visibility from '@mui/icons-material/Visibility';
import { Tooltip } from '@efexteam/components';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { SSN_LENGTH } from '@pages/onboarding/Mexico/steps/StakeHolders/stakeholders.constants';
import { icon_check_circle_green } from '@/assets';
import styles from './styles';

const PersonalInfo = () => {
  const { t } = useTranslation();
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    control,
    watch,
    formState: { errors },
  } = useFormContext();

  const [
    nameField,
    lastNameField,
    citizenshipField,
    percentageField,
    titleField,
    birthDateField,
    genderField,
    ssnField,
  ] = watch([
    'name',
    'lastName',
    'countryBirthId',
    'percentage',
    'title',
    'birthDate',
    'gender',
    'ssn',
  ]);

  const { data: birthcountries, invoke: getBirthCountries } = useApiCall(
    (value: string) => authSvc.getCountries({ value })
  );

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const loadInitialCountry = useCallback(() => {
    if (citizenshipField && !hasLoadedInitial) {
      getBirthCountries(citizenshipField.toString());
      setHasLoadedInitial(true);
    }
  }, [citizenshipField, hasLoadedInitial, getBirthCountries]);

  useEffect(() => {
    loadInitialCountry();
  }, [loadInitialCountry]);

  return (
    <Box className="flex flex-col gap-4">
      <Grid container rowSpacing={2} columnSpacing={2}>
        <Grid item md={6} sm={12}>
          <FormControl fullWidth>
            <Typography variant="subtitle2">{t(`onbStHlName`)}</Typography>

            <Box
              sx={{
                display: 'flex',
              }}
            >
              <TextField
                autoComplete="off"
                type="text"
                className="custom-textfield w-[90%]"
                size="small"
                placeholder={t(`onbStHlName`) as string}
                helperText={t(`onbStHlNameMessage`) as string}
                error={!!errors.name}
                {...register('name', {
                  required: true,
                  maxLength: TEXTS_MAX_LENGTH,
                  pattern: {
                    value: onlyTextRegex,
                    message: '',
                  },
                })}
              />

              {nameField?.length > 0 && !errors.name && (
                <img
                  src={icon_check_circle_green}
                  alt=""
                  className="w-[25px]"
                />
              )}
            </Box>

            {errors && errors.name?.type === 'required' && (
              <p className="text-orange-600 text-xs mt-1">
                {t('defaultError')}
              </p>
            )}
            {errors && errors.name?.type === 'pattern' && (
              <p className="text-orange-600 text-xs mt-1">
                {t('onbOnlyTextError')}
              </p>
            )}
            {errors && errors.name?.type === 'maxLength' && (
              <p className="text-orange-600 text-xs mt-1">
                {t('onbMaxDigits', { digits: TEXTS_MAX_LENGTH })}
              </p>
            )}
          </FormControl>
        </Grid>

        <Grid item md={6} sm={12}>
          <FormControl fullWidth>
            <Typography variant="subtitle2">{t(`onbStHlLastName`)}</Typography>

            <Box sx={{ display: 'flex' }}>
              <TextField
                autoComplete="off"
                type="text"
                className="custom-textfield w-[90%]"
                size="small"
                placeholder={t(`onbStHlLastName`) as string}
                helperText={t(`onbStHlNameMessage`) as string}
                error={!!errors.lastName}
                {...register('lastName', {
                  required: false,
                  maxLength: TEXTS_MAX_LENGTH,
                  pattern: {
                    value: onlyTextRegex,
                    message: '',
                  },
                })}
              />

              {lastNameField?.length > 0 && !errors.lastName && (
                <img
                  src={icon_check_circle_green}
                  alt=""
                  className="w-[25px] ml-[10px]"
                />
              )}
            </Box>

            {errors && errors.lastName?.type === 'required' && (
              <p className="text-orange-600 text-xs mt-1">
                {t('defaultError')}
              </p>
            )}
            {errors && errors.lastName?.type === 'pattern' && (
              <p className="text-orange-600 text-xs mt-1">
                {t('onbOnlyTextError')}
              </p>
            )}
            {errors && errors.lastName?.type === 'maxLength' && (
              <p className="text-orange-600 text-xs mt-1">
                {t('onbMaxDigits', { digits: TEXTS_MAX_LENGTH })}
              </p>
            )}
          </FormControl>
        </Grid>

        <Grid item md={6} sm={12}>
          <FormControl fullWidth>
            <Typography variant="subtitle2">
              {t(`onbStHlCitizenship`)}
            </Typography>

            <Box className="flex">
              <Controller
                name="countryBirthId"
                control={control}
                render={({ field: { onChange, value, ...props } }) => {
                  const selectedCountry =
                    (birthcountries as CountriesList[])?.find(
                      (country) => country.id === value
                    ) || null;

                  return (
                    <Autocomplete
                      {...props}
                      disablePortal
                      className="w-[90%]"
                      size="small"
                      getOptionLabel={(option) => option.name || ''}
                      options={(birthcountries as CountriesList[]) ?? []}
                      value={selectedCountry}
                      onInputChange={(_, newInputValue, reason) => {
                        if (reason === 'input' && newInputValue) {
                          getBirthCountries(newInputValue);
                        }
                      }}
                      onChange={(_, selectedOption) => {
                        onChange(selectedOption?.id || null);
                      }}
                      isOptionEqualToValue={(option, selectedValue) =>
                        option.id === selectedValue?.id
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          error={!!errors.countryBirthId}
                          autoComplete="off"
                          placeholder={t(`onbStHlCitizenship`) as string}
                          inputProps={{
                            ...params.inputProps,
                            autoComplete: 'off',
                          }}
                        />
                      )}
                      renderOption={(innerProps, option) => (
                        <Box component="li" className="px-3" {...innerProps}>
                          <div>{option.name}</div>
                        </Box>
                      )}
                    />
                  );
                }}
              />

              {citizenshipField && !errors.countryBirthId && (
                <img
                  src={icon_check_circle_green}
                  alt=""
                  className="w-[25px] ml-[10px]"
                />
              )}
            </Box>

            {errors && errors.countryBirthId?.type === 'required' && (
              <p className="text-orange-600 text-xs mt-1">
                {t('defaultError')}
              </p>
            )}
            {errors && errors.countryBirthId?.type === 'pattern' && (
              <p className="text-orange-600 text-xs mt-1">
                {t('onbOnlyTextError')}
              </p>
            )}
          </FormControl>
        </Grid>

        {citizenshipField === 229 && (
          <Grid item md={6} sm={12}>
            <FormControl fullWidth>
              <Box className="flex items-center gap-2">
                <Typography variant="subtitle2">{t(`onbSsn`)}</Typography>
                <Tooltip title={t('onbSsnTooltip')}>
                  <LockOutlinedIcon fontSize="small" />
                </Tooltip>
              </Box>

              <Box sx={{ display: 'flex' }}>
                <TextField
                  autoComplete="off"
                  type={showPassword ? 'text' : 'password'}
                  className="custom-textfield w-[90%]"
                  size="small"
                  placeholder={t(`onbSsn`) as string}
                  error={!!errors.ssn}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                  {...register('ssn', {
                    required: true,
                    pattern: {
                      value: onlyNumbersRegex,
                      message: '',
                    },
                    maxLength: SSN_LENGTH,
                    minLength: SSN_LENGTH,
                  })}
                />

                {ssnField?.length > 0 && !errors.ssn && (
                  <img
                    src={icon_check_circle_green}
                    alt=""
                    className="w-[25px] ml-[10px]"
                  />
                )}
              </Box>

              {errors && errors.ssn?.type === 'required' && (
                <p className="text-orange-600 text-xs mt-1">
                  {t('defaultError')}
                </p>
              )}
              {(errors.ssn?.type === 'maxLength' ||
                errors.ssn?.type === 'minLength') && (
                <p className="text-orange-600 text-xs mt-1">
                  {t('onbMaxDigits', { digits: SSN_LENGTH })}
                </p>
              )}
            </FormControl>
          </Grid>
        )}

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <Typography variant="subtitle2">
              {t(`onbStHlPercentage`)}
            </Typography>

            <Box sx={{ display: 'flex' }}>
              <TextField
                autoComplete="off"
                type="number"
                className="custom-textfield w-[90%]"
                size="small"
                placeholder="50"
                error={!!errors.percentage}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                }}
                {...register('percentage', {
                  required: true,
                  pattern: {
                    value: onlyNumbersRegex,
                    message: '',
                  },
                  max: 100,
                })}
              />

              {percentageField?.length > 0 && !errors.percentage && (
                <img
                  src={icon_check_circle_green}
                  alt=""
                  className="w-[25px] ml-[10px]"
                />
              )}
            </Box>

            {errors && errors.percentage?.type === 'required' && (
              <p className="text-orange-600 text-xs mt-1">
                {t('defaultError')}
              </p>
            )}
            {errors && errors.percentage?.type === 'pattern' && (
              <p className="text-orange-600 text-xs mt-1">
                {t('onbOnlyNumberError')}
              </p>
            )}
            {errors && errors.percentage?.type === 'max' && (
              <p className="text-orange-600 text-xs mt-1">
                {t('onbPercentageError')}
              </p>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <Typography variant="subtitle2">{t(`onbStHlTitle`)}</Typography>

            <Box sx={{ display: 'flex' }}>
              <TextField
                autoComplete="off"
                type="text"
                className="custom-textfield w-[90%]"
                size="small"
                placeholder={t('onbStHlTitle') as string}
                error={!!errors.title}
                {...register('title', {
                  required: true,
                  maxLength: TEXTS_MAX_LENGTH,
                  pattern: {
                    value: onlyTextRegex,
                    message: '',
                  },
                })}
              />
              {titleField?.length > 0 && !errors.title && (
                <img
                  src={icon_check_circle_green}
                  alt=""
                  className="w-[25px] ml-[10px]"
                />
              )}
            </Box>

            {errors && errors.title?.type === 'required' && (
              <p className="text-orange-600 text-xs mt-1">
                {t('defaultError')}
              </p>
            )}
            {errors && errors.title?.type === 'pattern' && (
              <p className="text-orange-600 text-xs mt-1">
                {t('onbOnlyTextError')}
              </p>
            )}
            {errors && errors.title?.type === 'maxLength' && (
              <p className="text-orange-600 text-xs mt-1">
                {t('onbMaxDigits', { digits: TEXTS_MAX_LENGTH })}
              </p>
            )}
          </FormControl>
        </Grid>
      </Grid>

      <Grid container rowSpacing="auto" columnSpacing={2}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <Typography variant="subtitle2">{t(`onbStHlBirthDate`)}</Typography>

            <Box sx={{ display: 'flex', height: '40px' }}>
              <Controller
                name="birthDate"
                rules={{ required: true }}
                control={control}
                render={({ field }) => (
                  <LocalizationProvider dateAdapter={AdapterLuxon}>
                    <DatePicker
                      {...field}
                      disableFuture
                      maxDate={DateTime.fromJSDate(eighteenYearsAgo)}
                      className={styles.dropdownStyles}
                      onChange={(value) => {
                        if (!value) return;
                        field.onChange(value);
                      }}
                      value={
                        field.value
                          ? DateTime.fromISO(field.value as string)
                          : null
                      }
                      slotProps={{
                        // eslint-disable-next-line react/no-unstable-nested-components
                        textField: (params) => (
                          <TextField
                            {...params}
                            size="small"
                            error={!!errors.birthDate}
                            fullWidth
                          />
                        ),
                      }}
                    />
                  </LocalizationProvider>
                )}
              />

              {birthDateField && !errors.birthDate && (
                <img
                  src={icon_check_circle_green}
                  alt=""
                  className="w-[25px] ml-[10px]"
                />
              )}
            </Box>

            {errors && errors.birthDate && (
              <p className="text-orange-600 text-xs mt-1">
                {t('defaultError')}
              </p>
            )}
          </FormControl>
        </Grid>

        <Grid className="!mt-2 md:!mt-0" item xs={12} md={6}>
          <Typography variant="subtitle2">{t(`regGender`)}</Typography>
          <FormControl fullWidth>
            <Box sx={{ display: 'flex' }}>
              <Controller
                name="gender"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select
                    labelId="gender"
                    id="gender"
                    className={styles.dropdownStyles}
                    label={t('regGender')}
                    error={!!errors.gender}
                    value={field.value || ''}
                    onChange={field.onChange}
                  >
                    {GENDER_OPTIONS.map((genderOption) => (
                      <MenuItem key={genderOption.key} value={genderOption.key}>
                        {t(genderOption.value)}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />

              {genderField?.length > 0 && !errors.gender && (
                <img
                  src={icon_check_circle_green}
                  alt=""
                  className="w-[25px] ml-[10px]"
                />
              )}
            </Box>

            {errors.gender && (
              <p className="text-orange-600 text-xs">
                {t('regGenderValidation')}
              </p>
            )}
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PersonalInfo;
